import { useCallback, useContext, useMemo } from 'react';
import { useRouter } from 'expo-router';

import { LoginMountContext } from '../contexts/LoginMount';

/**
 * Resolve a login-relative path against the consumer's mount base.
 *
 *   base='/', path='./'                 -> '/'
 *   base='/', path='./sso-login'        -> '/sso-login'
 *   base='/auth/apigee', path='./'      -> '/auth/apigee'
 *   base='/auth/apigee', path='./x'     -> '/auth/apigee/x'
 *   base='/auth/apigee/', path='./x'    -> '/auth/apigee/x'   (trailing slash normalized)
 *   base=anything, path='/absolute'     -> '/absolute'   (pass-through)
 *   base=anything, path='https://...'   -> 'https://...' (pass-through)
 */
export const resolveLoginPath = (base: string, path: string): string => {
  // Normalize a trailing slash on the mount base so consumers can pass
  // either '/auth/apigee' or '/auth/apigee/' without producing double
  // slashes on relative paths ('/auth/apigee//sso-login').
  const normalizedBase = base === '/' ? '/' : base.replace(/\/+$/, '');
  if (path === './') return normalizedBase || '/';
  if (path.startsWith('./')) {
    const prefix = normalizedBase === '/' ? '' : normalizedBase;
    return prefix + '/' + path.slice(2);
  }
  return path;
};

/**
 * Drop-in replacement for expo-router's `useRouter` inside login. Wraps
 * `push` and `replace` so callers can pass login's relative ROUTES ('./',
 * './sso-login', ...) unchanged — the hook resolves them to absolute paths
 * against the mount base from <LoginMountProvider>.
 *
 * Motivation: Expo Router's imperative `router.push('./x')` does not
 * reliably resolve on native (the pathname context isn't available in
 * async callbacks). The declarative <Link href='./x'> works because it
 * reads route context at render, but login has plenty of imperative nav
 * (post-login redirect, magic-link-sent auto-redirect, form-submit
 * callbacks). Rather than sprinkle useSegments() at every call site,
 * everything channels through this hook.
 *
 * Non-relative paths ('/x', 'https://…') pass through unchanged, so
 * external URLs and anchor links still work.
 */
/**
 * Resolve a router href — string or object form — against the mount base.
 * Object-form hrefs `{ pathname, params?, ... }` are common when callers
 * want to pass typed params alongside the route; without this branch the
 * relative `pathname` would leak through untouched and break on native
 * under a subpath mount. Non-string / non-object shapes pass through as-is.
 */
const resolveHref = <T>(href: T, resolve: (path: string) => string): T => {
  if (typeof href === 'string') return resolve(href) as unknown as T;
  if (href && typeof href === 'object' && 'pathname' in href) {
    const withPathname = href as { pathname?: unknown };
    if (typeof withPathname.pathname === 'string') {
      return { ...(href as object), pathname: resolve(withPathname.pathname) } as T;
    }
  }
  return href;
};

export const useLoginRouter = () => {
  const router = useRouter();
  const base = useContext(LoginMountContext);

  const resolve = useCallback((path: string) => resolveLoginPath(base, path), [base]);

  return useMemo(
    () => ({
      ...router,
      push: (href: Parameters<typeof router.push>[0]) =>
        router.push(resolveHref(href, resolve) as never),
      replace: (href: Parameters<typeof router.replace>[0]) =>
        router.replace(resolveHref(href, resolve) as never),
      navigate: (href: Parameters<typeof router.navigate>[0]) =>
        router.navigate(resolveHref(href, resolve) as never),
    }),
    [router, resolve]
  );
};
